/**
 * 1.81.0 · Servidor local: el protocolo entre el servidor y los puestos, sin depender del transporte
 * (en Windows viaja por TCP con lan.rs; en las pruebas, en memoria).
 *
 * Saludo (sin cifrar, no lleva datos del negocio salvo su id):
 *   puesto → { t:"hello", v, pv, pmin, sh, name, biz, dev?, n?, epk? | pk? }
 *     · 1.99.5: `pv`/`pmin`/`sh` (protocolo y huella de la base, ver compat.ts): con versiones distintas pero
 *       compatibles se trabaja igual.
 *     · con `dev` (ya vinculado): el servidor responde { t:"resume", n, epk, srv } y desde ahí todo va cifrado.
 *       1.99.5: `epk` es una clave ECDH nueva de cada lado para esta conexión (entra en la clave de sesión).
 *     · con `pk` (vincular): el servidor responde { t:"pair", pk, srv }; las dos puntas ven el mismo código de
 *       6 dígitos. El puesto manda { t:"pair-ask" } (se acepta en el servidor) o { t:"pair-code", proof }
 *       (el código que muestra el servidor). Si va bien: { t:"paired", iv, c } con la clave propia del puesto.
 *     · errores: { t:"err", code: version | business | unknown | rejected | code | busy, msg }
 * Sesión: { t:"s", iv, c } con AES-GCM; el número de orden de cada mensaje va en los datos autenticados,
 * así no se puede repetir ni reordenar un mensaje grabado.
 *   pedido { id, m, a } → respuesta { id, r } o { id, e } · aviso del servidor { ev, d }
 */
import {
  aesKey, codeProof, ecdhPair, ecdhSecret, lanTag, open, pairingKey, pairingSas, randomB64, sameText, seal, sessionKey2,
  type EcdhPair, type Sealed,
} from "./crypto";
import { compatible, handshakeTranscript, LAN_PROTOCOL, LAN_PROTOCOL_MIN, peerCompat } from "./compat";

export interface Wire {
  send(text: string): Promise<void>;
  close(): void;
}

export interface ServerIdentity { id: string; name: string; businessId: string; locationId: string | null; version: string;
  /** 1.99.5: huella del esquema de la base (compat.ts). */
  schema?: string }
export interface PairedDevice { id: string; key: string; name: string; pairedAt: string }

export type LanErrorCode = "version" | "business" | "unknown" | "rejected" | "code" | "busy" | "closed" | "protocol" | "timeout";
export class LanError extends Error {
  constructor(public code: LanErrorCode, message: string, public extra?: Record<string, unknown>) { super(message); }
}

const ERR_TEXT: Record<LanErrorCode, string> = {
  version: "El puesto y el servidor tienen versiones que no pueden trabajar juntas: actualizá el que tenga la versión vieja.",
  business: "Ese servidor es de otro negocio.",
  unknown: "El servidor no reconoce este puesto: hay que vincularlo de nuevo.",
  rejected: "El servidor rechazó la conexión.",
  code: "El código no es el que muestra el servidor.",
  busy: "El servidor está atendiendo otra vinculación: probá de nuevo en un momento.",
  closed: "Se cortó la conexión con el servidor.",
  protocol: "El servidor respondió algo inesperado.",
  timeout: "El servidor no respondió a tiempo.",
};
export const lanErrorText = (c: LanErrorCode) => ERR_TEXT[c];

/** Mensaje cifrado con número de orden (por sentido). */
class Session {
  private out = 0;
  private in = 0;
  constructor(private key: CryptoKey, private mine: "c" | "s") {}
  static async create(raw: Uint8Array, mine: "c" | "s") { return new Session(await aesKey(raw), mine); }
  async wrap(obj: unknown): Promise<string> {
    const n = ++this.out;
    const s = await seal(this.key, JSON.stringify(obj), `${this.mine}:${n}`);
    return JSON.stringify({ t: "s", ...s });
  }
  async unwrap(msg: Sealed): Promise<unknown> {
    const n = this.in + 1;
    const text = await open(this.key, msg, `${this.mine === "c" ? "s" : "c"}:${n}`);
    this.in = n;
    return JSON.parse(text);
  }
}

// ---------------------------------------------------------------------------------------------------
// Servidor

export interface HostDeps {
  /** 1.98.0: tiempos de corte (para las pruebas; por defecto HELLO_WAIT_MS e IDLE_MS). */
  helloWaitMs?: number;
  pairWaitMs?: number;
  idleMs?: number;
  identity: () => ServerIdentity;
  device: (id: string) => Promise<PairedDevice | null>;
  addDevice: (d: PairedDevice) => Promise<void>;
  touchDevice?: (id: string, info: { name: string; ip: string; version: string }) => void;
  /** 1.81.7: se conectó un puesto con otra versión (el servidor se actualiza ya si el puesto es más nuevo). */
  onVersion?: (peerVersion: string) => void;
  /** Código de 6 dígitos que muestra el servidor ahora (null = no se puede vincular con código). */
  currentCode: () => string | null;
  /** Tres intentos fallidos: el servidor cambia el código. */
  onBadCode?: () => void;
  /** 1.99.19: código de verificación del puesto que se está vinculando ahora (null = ninguno), para mostrarlo junto al código. */
  onPairingSas?: (sas: string | null) => void;
  /** Pantalla del servidor: «CAJA-2 quiere conectarse · código 123456 · Aceptar / Rechazar». */
  askApproval: (r: { conn: string; name: string; ip: string; sas: string }) => Promise<boolean>;
  /** Pedido del puesto ya autenticado. */
  handle: (peer: HostPeer, method: string, args: unknown) => Promise<unknown>;
  onReady?: (peer: HostPeer) => void;
  onGone?: (peer: HostPeer) => void;
  uuid: () => string;
  now?: () => Date;
}

export interface HostPeer { conn: string; ip: string; deviceId: string; name: string; version: string; push: (ev: string, d?: unknown) => Promise<void> }

interface Conn {
  wire: Wire;
  ip: string;
  phase: "hello" | "pair" | "ready" | "closed";
  name: string;
  version: string;
  ecdh?: EcdhPair;
  clientPk?: string;
  secret?: Uint8Array;
  sas?: string;
  session?: Session;
  /** Ya mandó un mensaje cifrado válido (tiene la clave): recién ahí cuenta como conectado. */
  authed?: boolean;
  peer?: HostPeer;
  queue: Promise<void>;
  /** Los mensajes cifrados salen de a uno y en orden (cada uno lleva su número). */
  out: Promise<void>;
  /** 1.98.0: corta si no saluda a tiempo o, ya lista, si deja de mandar mensajes (el latido incluido). */
  timer?: ReturnType<typeof setTimeout>;
  /** 1.99.20: antes de identificarse tiene un solo plazo (mandar mensajes no lo estira). */
  preArmed?: boolean;
}

/** 1.99.20: lo más grande que acepta el servidor antes de que el puesto se identifique (un saludo es chico). */
const PRE_AUTH_MAX = 16_384;
/** 1.99.20: pedidos de «Aceptar en el servidor» por dirección: como mucho 3 cada 10 minutos. */
const ASK_MAX = 3;
const ASK_WINDOW_MS = 10 * 60_000;

export class LanHost {
  private conns = new Map<string, Conn>();
  private asks = new Map<string, number[]>();
  private pairing: string | null = null;
  private badCodes = 0;
  constructor(private deps: HostDeps) {}

  open(conn: string, ip: string, wire: Wire) {
    const c: Conn = { wire, ip, phase: "hello", name: "", version: "", queue: Promise.resolve(), out: Promise.resolve() };
    this.conns.set(conn, c);
    this.arm(conn, c, this.deps.helloWaitMs ?? HELLO_WAIT_MS);
  }

  private arm(conn: string, c: Conn, ms: number) {
    if (c.timer) clearTimeout(c.timer);
    c.timer = ms > 0 ? setTimeout(() => { if (this.conns.get(conn) === c) this.drop(conn, c); }, ms) : undefined;
  }

  /** 1.98.0: al apagar el servidor, se olvidan todas las conexiones (y sus relojes de corte). */
  closeAll() {
    for (const [k, c] of [...this.conns]) { if (c.timer) clearTimeout(c.timer); c.phase = "closed"; this.conns.delete(k); }
    if (this.pairing) this.deps.onPairingSas?.(null);
    this.pairing = null;
  }

  close(conn: string) {
    const c = this.conns.get(conn);
    if (!c) return;
    if (c.timer) clearTimeout(c.timer);
    c.phase = "closed";
    this.conns.delete(conn);
    if (this.pairing === conn) { this.pairing = null; this.deps.onPairingSas?.(null); }
    if (c.peer && c.authed) this.deps.onGone?.(c.peer);
  }

  peers(): HostPeer[] {
    return [...this.conns.values()].flatMap((c) => (c.peer && c.phase === "ready" && c.authed ? [c.peer] : []));
  }

  /** Aviso a todos los puestos conectados (por ejemplo, «cambiaron estas tablas»). */
  async broadcast(ev: string, d?: unknown) {
    await Promise.all(this.peers().map((p) => p.push(ev, d).catch(() => undefined)));
  }

  /** Mensaje de un puesto. Se atienden de a uno por conexión, en orden. */
  receive(conn: string, text: string): Promise<void> {
    const c = this.conns.get(conn);
    if (!c) return Promise.resolve();
    // Lista: cada mensaje la mantiene viva. Antes de identificarse: un mensaje grande corta, y el plazo para vincular
    // (con «Aceptar» puede tardar) es uno solo desde el primer mensaje (1.99.20: antes cada mensaje lo estiraba).
    if (c.phase === "ready") this.arm(conn, c, this.deps.idleMs ?? IDLE_MS);
    else {
      if (text.length > PRE_AUTH_MAX) { this.drop(conn, c); return Promise.resolve(); }
      if (!c.preArmed) { c.preArmed = true; this.arm(conn, c, this.deps.pairWaitMs ?? PAIR_WAIT_MS); }
    }
    c.queue = c.queue.then(() => this.step(conn, c, text)).catch(() => this.drop(conn, c));
    return c.queue;
  }

  private async plain(c: Conn, obj: unknown) { await c.wire.send(JSON.stringify(obj)); }

  private sealed(c: Conn, obj: unknown): Promise<void> {
    c.out = c.out.then(async () => {
      if (c.phase !== "ready" || !c.session) return;
      let text = await c.session.wrap(obj);
      // Una respuesta demasiado grande no se manda (la conexión está bien): se contesta con un error.
      const id = (obj as { id?: unknown })?.id;
      if (text.length > MAX_SEND && typeof id === "number") text = await c.session.wrap({ id, e: "La respuesta es demasiado grande para la red del local: filtrá más la búsqueda" });
      await c.wire.send(text);
    }).catch(() => {
      // 1.98.0: si no se pudo mandar (respuesta demasiado grande, conexión rota), se corta: el puesto se entera
      // enseguida y reconecta (antes esperaba 30 s la respuesta que nunca iba a llegar).
      for (const [k, x] of this.conns) if (x === c) this.drop(k, c);
    });
    return c.out;
  }

  private async fail(conn: string, c: Conn, code: LanErrorCode, extra?: Record<string, unknown>) {
    await this.plain(c, { t: "err", code, msg: ERR_TEXT[code], ...extra }).catch(() => undefined);
    this.drop(conn, c);
  }

  private drop(conn: string, c: Conn) {
    try { c.wire.close(); } catch { /* ya cerrada */ }
    this.close(conn);
  }

  private async step(conn: string, c: Conn, text: string) {
    if (c.phase === "closed") return;
    let m: Record<string, unknown>;
    try { m = JSON.parse(text); } catch { return this.fail(conn, c, "protocol"); }
    const id = this.deps.identity();

    if (c.phase === "ready" && m.t === "s" && c.session && c.peer) {
      const req = (await c.session.unwrap(m as unknown as Sealed)) as { id?: number; m?: string; a?: unknown };
      if (typeof req.id !== "number" || typeof req.m !== "string") return;
      const peer = c.peer;
      if (!c.authed) {
        c.authed = true;
        this.deps.touchDevice?.(peer.deviceId, { name: c.name, ip: c.ip, version: c.version });
        this.deps.onReady?.(peer);
      }
      if (req.m === "hi" || req.m === "ping") { await this.sealed(c, { id: req.id, r: { ok: true } }); return; }
      // Los pedidos se contestan sin esperar al siguiente (una consulta larga no frena a las demás).
      void (async () => {
        let out: unknown;
        try { out = { id: req.id, r: await this.deps.handle(peer, req.m!, req.a) }; }
        catch (e) { out = { id: req.id, e: (e as Error)?.message ?? String(e) }; }
        await this.sealed(c, out);
      })();
      return;
    }

    if (m.t === "hello" && c.phase === "hello") {
      c.name = String(m.name ?? "Puesto").slice(0, 60);
      c.version = String(m.v ?? "");
      // 1.99.5: versiones distintas trabajan juntas si el protocolo y la base son compatibles. Igual, si el puesto
      // es más nuevo, el servidor se actualiza (y el puesto, si es el viejo, al ver la versión del servidor).
      const mine = { v: id.version, pv: LAN_PROTOCOL, pmin: LAN_PROTOCOL_MIN, sh: id.schema ?? "" };
      if (c.version !== id.version) this.deps.onVersion?.(c.version);
      const peerC = peerCompat(m);
      if (!compatible(mine, peerC)) return this.fail(conn, c, "version", { v: id.version });
      // 1.99.5: el puesto manda la huella del negocio (`bh`), no su id.
      const bizOk = typeof m.bh === "string" ? m.bh === await lanTag("biz", id.businessId) : m.biz === id.businessId;
      if (!bizOk) return this.fail(conn, c, "business");
      if (typeof m.dev === "string" && typeof m.n === "string") {
        if (typeof m.epk !== "string" || m.n.length > 64) return this.fail(conn, c, "protocol");
        const d = await this.deps.device(m.dev);
        if (!d) return this.fail(conn, c, "unknown");
        const n = randomB64(16);
        const eph = await ecdhPair();
        const secret = await ecdhSecret(eph.privateKey, m.epk);
        const tr = handshakeTranscript(peerC, { ...mine, id: id.id }, String(m.bh ?? m.biz ?? ""), m.dev);
        c.session = await Session.create(await sessionKey2(d.key, secret, m.n, n, m.epk, eph.publicB64, tr), "s");
        c.phase = "ready";
        const peer: HostPeer = {
          conn, ip: c.ip, deviceId: d.id, name: c.name, version: c.version,
          push: (ev, data) => this.sealed(c, { ev, d: data }),
        };
        c.peer = peer;
        await this.plain(c, { t: "resume", n, epk: eph.publicB64, srv: { id: id.id, name: id.name, v: id.version, pv: LAN_PROTOCOL, pmin: LAN_PROTOCOL_MIN, sh: mine.sh } });
        return;
      }
      if (typeof m.pk === "string") {
        if (this.pairing && this.pairing !== conn && this.conns.has(this.pairing)) return this.fail(conn, c, "busy");
        this.pairing = conn;
        c.ecdh = await ecdhPair();
        c.clientPk = m.pk;
        c.secret = await ecdhSecret(c.ecdh.privateKey, m.pk);
        c.sas = await pairingSas(c.secret, m.pk, c.ecdh.publicB64);
        c.phase = "pair";
        this.deps.onPairingSas?.(c.sas);
        await this.plain(c, { t: "pair", pk: c.ecdh.publicB64, srv: { id: id.id, name: id.name } });
        return;
      }
      return this.fail(conn, c, "protocol");
    }

    if (c.phase === "pair" && (m.t === "pair-ask" || m.t === "pair-code")) {
      const { secret, clientPk, ecdh, sas } = c;
      if (!secret || !clientPk || !ecdh || !sas) return this.fail(conn, c, "protocol");
      let ok = false;
      if (m.t === "pair-code") {
        const code = this.deps.currentCode();
        ok = !!code && sameText(String(m.proof ?? ""), await codeProof(secret, clientPk, ecdh.publicB64, code));
        if (!ok) {
          // Tres códigos equivocados (de cualquier puesto): el servidor cambia el código.
          if (++this.badCodes >= 3) { this.badCodes = 0; this.deps.onBadCode?.(); }
          return this.fail(conn, c, "code");
        }
        this.badCodes = 0;
      } else {
        // 1.99.20: una PC de la red no puede llenar la pantalla del servidor de pedidos.
        const now = Date.now();
        const recent = (this.asks.get(c.ip) ?? []).filter((t) => now - t < ASK_WINDOW_MS);
        if (recent.length >= ASK_MAX) {
          this.asks.set(c.ip, recent);
          return this.fail(conn, c, "busy", { msg: "Se pidió vincular muchas veces seguidas desde esta PC: probá de nuevo en unos minutos o usá el código del servidor." });
        }
        if (this.asks.size > 200) for (const [ip, ts] of this.asks) if (!ts.some((t) => now - t < ASK_WINDOW_MS)) this.asks.delete(ip);
        this.asks.set(c.ip, [...recent, now]);
        ok = await this.deps.askApproval({ conn, name: c.name, ip: c.ip, sas });
        if (c.phase !== "pair") return; // se cortó mientras se decidía
        if (!ok) return this.fail(conn, c, "rejected");
      }
      const dev: PairedDevice = { id: this.deps.uuid(), key: randomB64(32), name: c.name, pairedAt: (this.deps.now?.() ?? new Date()).toISOString() };
      await this.deps.addDevice(dev);
      const k = await aesKey(await pairingKey(secret, clientPk, ecdh.publicB64));
      const sealed = await seal(k, JSON.stringify({ dev: dev.id, key: dev.key, srv: { id: id.id, name: id.name } }), "paired");
      c.phase = "hello";
      c.preArmed = false; // 1.99.20: el saludo que sigue tiene su propio plazo
      c.ecdh = undefined; c.secret = undefined; c.clientPk = undefined; c.sas = undefined;
      if (this.pairing === conn) { this.pairing = null; this.deps.onPairingSas?.(null); }
      await this.plain(c, { t: "paired", ...sealed });
      return;
    }

    return this.fail(conn, c, "protocol");
  }
}

// ---------------------------------------------------------------------------------------------------
// Puesto

export interface Pairing { serverId: string; serverName: string; deviceId: string; key: string }

export interface ClientOpts {
  version: string;
  /** 1.99.5: huella del esquema de la base (compat.ts). */
  schema?: string;
  name: string;
  businessId: string;
  /** Vínculo guardado (null = hay que vincular). */
  pairing: Pairing | null;
  /** Vincular: "ask" (se acepta en el servidor) o el código que muestra el servidor. */
  pair?: { mode: "ask" } | { mode: "code"; code: string };
  /** Mientras se vincula: el código que tiene que coincidir con el del servidor. */
  onSas?: (sas: string) => void;
  /**
   * 1.99.19 · Con código: antes de mandar la prueba, la persona confirma que el código de verificación es el mismo que
   * muestra el servidor. Una PC que se hace pasar por el servidor tiene otro (y sin la prueba no puede adivinar el código).
   */
  confirmSas?: (sas: string) => Promise<boolean>;
  onPaired?: (p: Pairing) => void | Promise<void>;
  onEvent?: (ev: string, d: unknown) => void;
  timeoutMs?: number;
  /** 1.98.0: cada cuánto se pregunta «¿seguís ahí?» (0 = nunca). Sin respuesta en PING_WAIT_MS, se corta. */
  pingMs?: number;
  /** 1.98.0: la conexión se cortó desde este lado (latido sin respuesta, un paso de transacción vencido…). */
  onClosed?: (e: Error) => void;
}

/** 1.98.0: latido. El puesto pregunta cada 5 s; si el servidor no contesta en 12 s, la conexión se da por perdida. */
export const PING_MS = 5_000;
export const PING_WAIT_MS = 12_000;
/** El servidor corta lo que no saluda en 10 s, lo que queda a medio vincular más de 200 s (la aprobación espera
 *  hasta 180 s) y las conexiones listas sin ningún mensaje en 90 s (con la ventana minimizada, Windows puede
 *  espaciar el latido hasta un minuto). */
export const HELLO_WAIT_MS = 10_000;
/** Tope de un mensaje (el transporte acepta hasta 32 MB). */
export const MAX_SEND = 30 * 1024 * 1024;
export const PAIR_WAIT_MS = 200_000;
export const IDLE_MS = 90_000;

export class LanClient {
  private session?: Session;
  private pending = new Map<number, { ok: (v: unknown) => void; ko: (e: Error) => void; t: ReturnType<typeof setTimeout> }>();
  private seq = 0;
  private queue = Promise.resolve();
  private ecdh?: EcdhPair;
  private pairSecret?: Uint8Array;
  private serverPk?: string;
  private nonce = "";
  private settle?: { ok: () => void; ko: (e: Error) => void };
  private closed = false;
  private pinger?: ReturnType<typeof setInterval>;
  /** Cuándo llegó el último mensaje del servidor (cualquiera prueba que sigue vivo). */
  private heard = Date.now();
  /** El transporte avisó que se cortó, o la app la cerró (eso ya lo atiende el puesto: no se repite con onClosed). */
  private transportGone = false;
  /** 1.99.5: la clave ECDH de esta conexión (para la sesión). */
  private eph?: EcdhPair;
  /** 1.99.5: `pv`: el protocolo del servidor (para no pedirle algo que no conoce). */
  server: { id: string; name: string; v?: string; pv?: number } | null = null;
  private bizTag = "";

  constructor(private wire: Wire, private o: ClientOpts) {}

  /** Saluda y deja la sesión lista (vinculando antes si hace falta). */
  start(): Promise<void> {
    return new Promise<void>((ok, ko) => {
      const t = setTimeout(() => ko(new LanError("timeout", ERR_TEXT.timeout)), this.o.timeoutMs ?? (this.o.pair?.mode === "ask" || (this.o.pair && this.o.confirmSas) ? 180_000 : 15_000));
      this.settle = { ok: () => { clearTimeout(t); ok(); }, ko: (e) => { clearTimeout(t); ko(e); } };
      void this.hello().catch((e) => this.settle?.ko(e));
    });
  }

  private async hello() {
    this.bizTag = await lanTag("biz", this.o.businessId);
    const base = { t: "hello", v: this.o.version, pv: LAN_PROTOCOL, pmin: LAN_PROTOCOL_MIN, sh: this.o.schema ?? "", name: this.o.name, bh: this.bizTag };
    if (this.o.pairing) {
      this.nonce = randomB64(16);
      this.eph = await ecdhPair();
      await this.wire.send(JSON.stringify({ ...base, dev: this.o.pairing.deviceId, n: this.nonce, epk: this.eph.publicB64 }));
    } else {
      if (!this.o.pair) throw new LanError("unknown", ERR_TEXT.unknown);
      this.ecdh = await ecdhPair();
      await this.wire.send(JSON.stringify({ ...base, pk: this.ecdh.publicB64 }));
    }
  }

  receive(text: string): Promise<void> {
    this.heard = Date.now();
    this.queue = this.queue.then(() => this.step(text)).catch((e) => this.fatal(e instanceof LanError ? e : new LanError("protocol", ERR_TEXT.protocol)));
    return this.queue;
  }

  /** Se cortó la conexión. */
  gone() {
    this.transportGone = true;
    // Después de lo que ya llegó (el error que explica por qué se cortó, si lo hubo).
    this.queue = this.queue.then(() => this.fatal(new LanError("closed", ERR_TEXT.closed)), () => undefined);
  }

  private startPing() {
    const every = this.o.pingMs ?? PING_MS;
    if (!every || this.pinger) return;
    this.pinger = setInterval(() => { if (!this.closed) void this.call("ping", undefined, PING_WAIT_MS).catch(() => undefined); }, every);
  }

  private fatal(e: Error) {
    if (this.pinger) { clearInterval(this.pinger); this.pinger = undefined; }
    if (this.closed) return;
    this.closed = true;
    const wasReady = !!this.session;
    this.settle?.ko(e);
    for (const p of this.pending.values()) { clearTimeout(p.t); p.ko(e); }
    this.pending.clear();
    try { this.wire.close(); } catch { /* ya cerrada */ }
    // Cerrar desde acá no genera el aviso de «se cortó» del transporte: se avisa a quien usa el puesto.
    if (wasReady && !this.transportGone) { try { this.o.onClosed?.(e); } catch { /* sin aviso */ } }
  }

  private async step(text: string) {
    const m = JSON.parse(text) as Record<string, unknown>;
    if (m.t === "s" && this.session) {
      const x = (await this.session.unwrap(m as unknown as Sealed)) as { id?: number; r?: unknown; e?: string; ev?: string; d?: unknown };
      if (typeof x.ev === "string") { this.o.onEvent?.(x.ev, x.d); return; }
      const p = typeof x.id === "number" ? this.pending.get(x.id) : undefined;
      if (!p) return;
      this.pending.delete(x.id!);
      clearTimeout(p.t);
      if (typeof x.e === "string") p.ko(new Error(x.e)); else p.ok(x.r);
      return;
    }
    if (m.t === "err") {
      // 1.99.15: con la sesión cifrada ya armada, un error sin cifrar no vale (cualquiera en la red podría mandarlo).
      if (this.session) return;
      const code = (String(m.code) as LanErrorCode) in ERR_TEXT ? (String(m.code) as LanErrorCode) : "protocol";
      const e = new LanError(code, String(m.msg ?? ERR_TEXT[code]), m);
      throw e;
    }
    if (m.t === "resume" && this.o.pairing && typeof m.n === "string") {
      if (!this.eph || typeof m.epk !== "string") throw new LanError("protocol", ERR_TEXT.protocol);
      const srv = (m.srv ?? {}) as Record<string, unknown>;
      const mine = { v: this.o.version, pv: LAN_PROTOCOL, pmin: LAN_PROTOCOL_MIN, sh: this.o.schema ?? "" };
      const theirs = peerCompat(srv);
      // Las dos puntas miran si son compatibles (lo que dijo el servidor entra en la clave: no se puede cambiar).
      if (!compatible(mine, theirs)) throw new LanError("version", ERR_TEXT.version, { v: theirs.v });
      const secret = await ecdhSecret(this.eph.privateKey, m.epk);
      const tr = handshakeTranscript(mine, { ...theirs, id: String(srv.id ?? "") }, this.bizTag, this.o.pairing.deviceId);
      this.session = await Session.create(await sessionKey2(this.o.pairing.key, secret, this.nonce, m.n, this.eph.publicB64, m.epk, tr), "c");
      this.eph = undefined;
      this.server = { id: String(srv.id ?? ""), name: String(srv.name ?? ""), v: theirs.v, pv: theirs.pv };
      if (this.server?.id !== this.o.pairing.serverId) throw new LanError("unknown", "Este no es el servidor con el que se vinculó el puesto.");
      // Las dos puntas prueban que tienen la clave antes de dar la conexión por buena.
      const settle = this.settle;
      void this.call("hi", undefined, 8_000).then(() => { this.startPing(); settle?.ok(); }, (e) => this.fatal(e));
      return;
    }
    if (m.t === "pair" && this.ecdh && typeof m.pk === "string" && this.o.pair) {
      const secret = await ecdhSecret(this.ecdh.privateKey, m.pk);
      this.server = m.srv as { id: string; name: string };
      const sas = await pairingSas(secret, this.ecdh.publicB64, m.pk);
      this.o.onSas?.(sas);
      this.pairSecret = secret;
      this.serverPk = m.pk;
      if (this.o.pair.mode === "code") {
        if (this.o.confirmSas && !(await this.o.confirmSas(sas))) {
          throw new LanError("rejected", "El código de verificación no es el del servidor: no se vinculó. Puede ser otra PC de la red haciéndose pasar por el servidor; revisá la IP.");
        }
        await this.wire.send(JSON.stringify({ t: "pair-code", proof: await codeProof(secret, this.ecdh.publicB64, m.pk, this.o.pair.code) }));
      } else {
        await this.wire.send(JSON.stringify({ t: "pair-ask" }));
      }
      return;
    }
    if (m.t === "paired" && this.ecdh) {
      if (!this.pairSecret || !this.serverPk) throw new LanError("protocol", ERR_TEXT.protocol);
      const k = await aesKey(await pairingKey(this.pairSecret, this.ecdh.publicB64, this.serverPk));
      const p = JSON.parse(await open(k, m as unknown as Sealed, "paired")) as { dev: string; key: string; srv: { id: string; name: string } };
      const pairing: Pairing = { serverId: p.srv.id, serverName: p.srv.name, deviceId: p.dev, key: p.key };
      await this.o.onPaired?.(pairing);
      this.o.pairing = pairing;
      this.ecdh = undefined; this.pairSecret = undefined; this.serverPk = undefined;
      await this.hello();
      return;
    }
    throw new LanError("protocol", ERR_TEXT.protocol);
  }

  get ready() { return !!this.session && !this.closed; }

  /** Pedido al servidor (consulta, escritura, transacción…). */
  async call<T = unknown>(m: string, a?: unknown, timeoutMs = 30_000): Promise<T> {
    if (!this.session || this.closed) throw new LanError("closed", ERR_TEXT.closed);
    const id = ++this.seq;
    const p = new Promise<T>((ok, ko) => {
      const t = setTimeout(() => {
        this.pending.delete(id);
        const e = new LanError("timeout", ERR_TEXT.timeout);
        ko(e);
        // 1.94.0: si vence un paso de una transacción, la conexión se corta: el servidor deshace esa
        // transacción y no queda trabada para los demás puestos (tx.status se usa para preguntar después).
        // 1.98.0: el latido sin respuesta (y sin ningún otro mensaje del servidor en ese tiempo) también corta: la
        // conexión está muerta aunque el socket siga abierto. Así aparece «Sin servidor» y se reconecta. Una
        // consulta lenta sola no corta (fallaría la venta que se está cobrando en este puesto).
        if ((m.startsWith("tx.") && m !== "tx.status") || (m === "ping" && Date.now() - this.heard >= PING_WAIT_MS)) this.fatal(e);
      }, timeoutMs);
      this.pending.set(id, { ok: ok as (v: unknown) => void, ko, t });
    });
    // 1.99.12: si la conexión se corta mientras el pedido todavía se está mandando, el rechazo llega antes de que
    // quien llamó lo espere: así no queda como «rechazo sin atender» (quien llama lo recibe igual).
    p.catch(() => undefined);
    // El cifrado lleva número de orden: los envíos se encadenan para que salgan en el mismo orden.
    const sent = this.queue.then(async () => { await this.wire.send(await this.session!.wrap({ id, m, a })); });
    // 1.86.0: si no se pudo mandar, la conexión queda cerrada (así se reconecta) y la cola no queda trabada.
    this.queue = sent.catch(() => undefined);
    try { await sent; } catch {
      p.catch(() => undefined);
      this.fatal(new LanError("closed", ERR_TEXT.closed));
      throw new LanError("closed", ERR_TEXT.closed);
    }
    return p;
  }

  /** Cierre pedido por la app (no avisa con onClosed). */
  close() { this.transportGone = true; this.fatal(new LanError("closed", ERR_TEXT.closed)); }
}
